import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { aboutInfo, faqs } from '@/mocks/anime';

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="风车动漫是国内领先的手机动漫门户网站，专注为移动端用户提供优质动漫资讯服务。了解我们的品牌故事、发展历程和联系方式。"
        keywords="风车动漫,关于我们,手机动漫,动漫门户,联系我们"
        canonicalUrl="https://fengchedongman.com/about"
        breadcrumbs={[
          { name: '首页', url: 'https://fengchedongman.com/' },
          { name: '关于我们', url: 'https://fengchedongman.com/about' },
        ]}
        faqData={faqs}
        type="webpage"
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" onClick={scrollToTop} className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto rounded-2xl bg-primary-100 text-primary-600 mb-6">
              <i className="ri-ancient-gate-line text-3xl md:text-4xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">关于风车动漫</h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.intro}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
              <div className="flex-1">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  风车动漫诞生于移动互联网蓬勃发展的时代。我们的创始团队发现，虽然动漫爱好者群体日益庞大，但市面上缺乏一个真正为手机用户打造的动漫内容平台。于是，风车动漫应运而生。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="w-full lg:w-[320px] flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20anime%20studio%20workspace%20with%20warm%20lighting%20colorful%20illustration%20boards%20on%20wall%20modern%20cozy%20office%20environment%20creative%20team%20atmosphere%20clean%20minimalist%20design&width=640&height=480&seq=about-brand-story&orientation=landscape"
                  alt="风车动漫品牌故事"
                  className="w-full rounded-lg object-cover aspect-[4/3]"
                  title="风车动漫 - 品牌故事"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">核心价值观</h2>
              <p className="text-sm text-foreground-500 mt-2">驱动我们不断前行的理念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.values.map((v, idx) => (
                <div key={idx} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                  <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`text-xl ${
                      idx === 0 ? 'ri-smartphone-line' : idx === 1 ? 'ri-file-text-line' : idx === 2 ? 'ri-user-heart-line' : 'ri-heart-pulse-line'
                    }`}></i>
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">发展历程</h2>
              <p className="text-sm text-foreground-500 mt-2">一路走来的成长足迹</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
              <div className="space-y-6">
                {aboutInfo.milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0 md:px-8`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 inline-block text-left">
                        <span className="text-xs text-primary-600 font-bold">{m.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{m.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-0 md:left-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 md:-translate-x-1/2 z-10">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">用户口碑</h2>
              <p className="text-sm text-foreground-500 mt-2">来自真实用户的评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.reviews.map((review, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className={`w-4 h-4 flex items-center justify-center ${i < review.rating ? 'text-primary-500' : 'text-foreground-200'}`}>
                        <i className={`text-xs ${i < review.rating ? 'ri-star-fill' : 'ri-star-line'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{review.content}&rdquo;</p>
                  <p className="text-xs text-foreground-400 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">常见问题</h2>
              <p className="text-sm text-foreground-500 mt-2">关于风车动漫的常见疑问</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</h3>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">联系我们</h2>
              <p className="text-sm text-foreground-500 mt-2">期待与您的交流</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                <a href={`mailto:${aboutInfo.contact.email}`} className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                  {aboutInfo.contact.email}
                </a>
              </div>
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-weibo-line text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">官方微博</h3>
                <span className="text-xs text-foreground-500">{aboutInfo.contact.weibo}</span>
              </div>
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">微信公众号</h3>
                <span className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</span>
              </div>
            </div>
            <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
              <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-600 mb-2">
                <i className="ri-map-pin-line text-lg"></i>
              </span>
              <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
              <p className="text-xs text-foreground-500">{aboutInfo.contact.address}</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}